import {
  DropzoneReadonly,
  FiFileText,
  FiSearch,
  GridItem,
  NumericFormatField,
  NumericReadOnlyField,
  ReadOnlyField,
  SelectField,
  TextField,
} from '@agrotoken-tech/ui';
import { S3FileUploader } from '@bcba/frontend-utils';
import { useTranslation } from '@bcba/react-hooks';
import { FileMimeType, geoDocumentationMimeTypes } from '@bcba/types';
import { getUrlFileFromAws } from '../../../../../../services/aws/getUrlFileFromAws';
import { getS3SignedUrl } from '../../../../../../services/documents/getS3SignedUrl';
import { useCropPledgeFarmContext } from '../../context/CropPledgeFarmInformationContext';

const FarmDataForm = () => {
  const { t } = useTranslation();
  const {
    provincesOptions,
    getProvinceLocalitiesOptions,
    hasSelectedProvince,
    formMethods,
    data,
    isFormReadOnly,
  } = useCropPledgeFarmContext();

  const { resetField, setValue, watch } = formMethods;

  const fileName = data?.geolocationDocument?.document?.originalFileName || '';

  const documentFromDatabase = data?.geolocationDocument;

  const shouldShowReadOnlyDropzoneField =
    isFormReadOnly ||
    (documentFromDatabase &&
      watch(`geolocationDocument.uuid`) === documentFromDatabase.uuid);

  return (
    <>
      <TextField
        name={'farmIdentifier'}
        label={t('cropPledge.farmInfo.form.identifier.label')}
        placeholder={t('cropPledge.farmInfo.form.identifier.placeholder')}
        errorMessageFormatter={t}
        rhfOptions={{ required: true }}
        isReadOnly={isFormReadOnly}
        readOnlyValue={data?.farmIdentifier ?? '-'}
      />
      <TextField
        name={'cadastralNomenclature'}
        label={t('cropPledge.farmInfo.form.cadastralNomenclature.label')}
        placeholder={t(
          'cropPledge.farmInfo.form.cadastralNomenclature.placeholder',
        )}
        errorMessageFormatter={t}
        rhfOptions={{ required: true }}
        isReadOnly={isFormReadOnly}
        readOnlyValue={data?.cadastralNomenclature ?? '-'}
      />
      <NumericFormatField
        name={'totalHectares'}
        label={t('cropPledge.farmInfo.form.totalHectares.label')}
        placeholder={t('cropPledge.farmInfo.form.totalHectares.placeholder')}
        errorMessageFormatter={t}
        rhfOptions={{ required: true }}
        isReadOnly={isFormReadOnly}
        readOnlyValue={data?.totalHectares ?? '-'}
      />
      {Boolean(provincesOptions?.length) && (
        <>
          <SelectField
            name="provinceUuid"
            label={t('cropPledge.farmInfo.form.province.label')}
            placeholder={t('cropPledge.farmInfo.form.province.placeholder')}
            errorMessageFormatter={t}
            options={provincesOptions}
            defaultInputValue={data?.province?.name}
            rhfOptions={{ required: true }}
            isReadOnly={isFormReadOnly}
            readOnlyValue={data?.province?.name ?? '-'}
          />
          <SelectField
            name="localityUuid"
            label={t('cropPledge.farmInfo.form.locality.label')}
            placeholder={t('cropPledge.farmInfo.form.locality.placeholder')}
            errorMessageFormatter={t}
            options={
              data?.locality
                ? [
                    {
                      label: data.locality.name,
                      value: data.locality.uuid,
                    },
                  ]
                : []
            }
            isDisabled={!hasSelectedProvince}
            leftIcon={FiSearch}
            debounce={500}
            getOptions={getProvinceLocalitiesOptions}
            noOptionMessage={t('common.noOptionsMessage')}
            isClearable
            cacheOptions={false}
            defaultInputValue={data?.locality?.name}
            rhfOptions={{ required: true }}
            isReadOnly={isFormReadOnly}
            readOnlyValue={data?.locality?.name ?? '-'}
          />
        </>
      )}
      <TextField
        name={'address'}
        label={t('cropPledge.farmInfo.form.address.label')}
        placeholder={t('cropPledge.farmInfo.form.address.placeholder')}
        errorMessageFormatter={t}
        rhfOptions={{ required: true }}
        isReadOnly={isFormReadOnly}
        readOnlyValue={data?.address ?? '-'}
      />
      <TextField
        name={'postalCode'}
        label={t('cropPledge.farmInfo.form.postalCode.label')}
        placeholder={t('cropPledge.farmInfo.form.postalCode.placeholder')}
        errorMessageFormatter={t}
        rhfOptions={{ required: true }}
        isReadOnly={isFormReadOnly}
        readOnlyValue={data?.postalCode ?? '-'}
      />
      <NumericReadOnlyField
        label={t('cropPledge.farmInfo.form.productionEstimateOfLots.label')}
        value={
          data?.totalLotEstimatedProductionValue
            ? data.totalLotEstimatedProductionValue.toString()
            : t(
                'cropPledge.farmInfo.form.productionEstimateOfLots.toBeCompletedByBolsa',
              )
        }
      />
      <GridItem colSpan={2}>
        {shouldShowReadOnlyDropzoneField ? (
          <DropzoneReadonly
            fileName={fileName}
            label={t('cropPledge.farmInfo.form.geolocation.label')}
            onClick={async () => {
              if (data?.geolocationDocument?.document.uuid) {
                const url = await getUrlFileFromAws(
                  data?.geolocationDocument?.document.uuid,
                );
                window.open(url, '_blank');
              }
            }}
            {...(isFormReadOnly
              ? {}
              : {
                  onDelete: () => {
                    setValue(`geolocationDocument.uuid`, undefined, {
                      shouldDirty: true,
                    });
                    setValue(`geolocationDocument.file`, undefined, {
                      shouldDirty: true,
                    });
                  },
                })}
            leftIcon={FiFileText}
          />
        ) : (
          <S3FileUploader
            fieldPrefix="geolocationDocument.file.s3TempKey"
            label={t('cropPledge.farmInfo.form.geolocation.label')}
            getS3PreSignedUrl={getS3SignedUrl}
            onFileUploadSuccess={async (fileName, mimeType, s3TempKey) => {
              setValue(`geolocationDocument.file.name`, fileName, {
                shouldDirty: true,
              });
              setValue(
                `geolocationDocument.file.mimeType`,
                mimeType as FileMimeType,
                { shouldDirty: true },
              );
              setValue(`geolocationDocument.file.s3TempKey`, s3TempKey, {
                shouldDirty: true,
              });
              setValue(`geolocationDocument.keepOriginal`, false, {
                shouldDirty: true,
              });
            }}
            mimeTypes={geoDocumentationMimeTypes}
            messages={{
              supportedFileTypes: t(
                'cropPledge.farmInfo.form.geolocation.supportedFileTypes',
              ),
            }}
            onDeleteClick={() => {
              resetField(`geolocationDocument.file.s3TempKey`);
              resetField(`geolocationDocument.file.name`);
              resetField(`geolocationDocument.file.mimeType`);
              resetField(`geolocationDocument.keepOriginal`, {
                defaultValue: false,
              });
            }}
            required
          />
        )}
      </GridItem>
      <ReadOnlyField
        label={t('cropPledge.farmInfo.form.latitudeOfBarycenter.label')}
        value={data?.latitude?.toString() ?? t('common.toBeCompletedByBolsa')}
      />
      <ReadOnlyField
        label={t('cropPledge.farmInfo.form.longitudeOfBarycenter.label')}
        value={data?.longitude?.toString() ?? t('common.toBeCompletedByBolsa')}
      />
    </>
  );
};

export default FarmDataForm;
